import {HttpClient} from '@angular/common/http';
import {Component} from '@angular/core';
import {Observable, of, tap} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import { OrganizationType } from './organization-type.enum';

@Component({
    selector: 'app-organization-list',
    template: `
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="page-title-box">
            <h4 class="page-title">Organization Management</h4>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <div class="row mb-2">
                <div class="col-12">
                  <a href="javascript:void(0);" class="btn btn-success mb-2 float-end"
                     data-bs-toggle="modal" data-bs-target="#CreateOrganization-modal"><i class="mdi mdi-plus-circle me-2"></i>
                    Create New Organization</a>
                </div>
              </div>
              <div class="table-responsive">
                <table class="table table-centered w-100 dt-responsive nowrap">
                  <thead class="table-light">
                  <tr>
                    <th>Display Name</th>
                    <th>Type</th>
                    <th>Created On</th>
                    <th>Last Updated</th>
                    <th>Active</th>
                    <th></th>
                  </tr>
                  </thead>
                  <tbody app-loading-shimmer [loading]="this.isLoading" [columns]="5">
                  <tr *ngFor="let item of this.organizations$ | async">
                    <td>{{ item.display_name }}</td>
                    <td>{{ item.type }}</td>
                    <td>{{ item.created_at | date }}</td>
                    <td>{{ item.updated_at | date }}</td>
                    <td>
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" role="switch" [(ngModel)]="item.is_active" (ngModelChange)="toggleOrganization(item.id, $event)">
                      </div>
                    </td>
                    <td>
                      <div class="row justify-content-end">
                        <div class="col-auto">
                          <a class="action-icon mdi mdi-square-edit-outline fs-4" role="button" [routerLink]="item.id"></a>
                        </div>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <div *ngIf="this.showError" class="alert alert-danger" role="alert">
                <h4 class="alert-heading">An error occurred while fetching organizations.</h4>
                <hr class="my-2">
                <p class="mb-0">If the error persists please contact us at <a class="text-danger" href="mailto:{{supportEmail}}">{{ supportEmail }}</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div id="CreateOrganization-modal" class="modal fade" tabindex="-1" style="display: none;" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header py-3 px-4 border-bottom-0">
            <h4 class="modal-title" id="standard-modalLabel">Create New Organization</h4>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"
                    (click)="closeModal()"></button>
          </div>
          <form class="ps-3 pe-3">
            <div class="modal-body">
              <div class="row g-2">
                <div class="col-12">
                  <div class="mb-3">
                    <label for="display_name" class="form-label">Display Name</label>
                    <input type="text" id="display_name" [(ngModel)]="this.newOrganizationRequest.display_name"
                           name="display_name"
                           class="form-control">
                  </div>
                  <div class="mb-3">
                    <label for="organization_type" class="form-label">Organization Type</label>
                    <select type="text" id="organization_type" [(ngModel)]="this.newOrganizationRequest.type"
                            name="organization_type"
                            class="form-control">
                      <option *ngFor="let type of this.organizationTypeOptions" [value]="type">{{ type }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer d-block">
              <div class="row g-2 text-end">
                <div class="mb-3 col-12">
                  <button type="submit" class="btn btn-primary" (click)="createOrganization()">Create</button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  `,
    styles: `
    .form-switch .form-check-input:checked {
      background-color: #42d29d;
      border-color: #42d29d;
    }
  `,
    standalone: false
})
export class OrganizationListComponent {
  // @ts-ignore
  private readonly ORGANIZATION_URL = `${Dmc.SUPPORT_API_ADDRESS}organizations`;
  protected readonly supportEmail = 'Support@DarkMatterIns.com';
  protected readonly organizationTypeOptions = Object.values(OrganizationType);
  protected readonly newOrganizationRequest = new NewOrganizationRequest();
  protected organizations$ = this.getOrganizations();
  protected isLoading = true;
  protected showError = false;

  constructor(private httpClient: HttpClient) {
    this.newOrganizationRequest.type = OrganizationType.GENERAL_AGENCY;
  }

  private getOrganizations(): Observable<Organization[]> {
    this.isLoading = true;
    const options = {params: {includeInactive: 'true'}};
    return this.httpClient.get<{ items: Organization[], count: number }>(this.ORGANIZATION_URL, options).pipe(
      switchMap(r => of(r.items)),
      catchError(() => {
        this.showError = true;
        return of([]);
      }),
      tap(_ => this.isLoading = false)
    );
  }

  protected toggleOrganization(id: string, isActive: Event): void {
    const options = {params: {includeInactive: 'true'}};
    this.organizations$ = this.httpClient.patch(`${this.ORGANIZATION_URL}/${id}`, {is_active: isActive}, options).pipe(
      switchMap(_ => this.getOrganizations())
    );
  }

  protected closeModal(): void {
    const element = document.getElementById('CreateOrganization-modal');
    // @ts-ignore
    const modal = bootstrap.Modal.getInstance(element);
    if (modal) {
      modal.hide();
    }
  }

  protected createOrganization(): void {
    if (!this.newOrganizationRequest.display_name || !this.newOrganizationRequest.type) {
      return;
    }

    this.organizations$ = this.httpClient.post(this.ORGANIZATION_URL, this.newOrganizationRequest).pipe(
      tap(_ => this.closeModal()),
      switchMap(_ => this.getOrganizations())
    );
  }
}

interface Organization {
  id: string;
  display_name: string;
  type: OrganizationType;
  is_active: boolean;
  updated_at: string;
  created_at: string;
}

class NewOrganizationRequest {
  display_name: string;
  type: OrganizationType;
}
